import { Capacitor } from '@capacitor/core';
import type { IAdProvider, AdReward, AdRewardType, AdStatus, AdProviderConfig } from './AdProvider.js';

type AdMobModule = typeof import('@capacitor-community/admob');

/**
 * AdMob Provider for Google AdMob rewarded ads
 */
export class AdMobProvider implements IAdProvider {
  readonly name = 'AdMobProvider';
  private _status: AdStatus = 'idle';
  private currentReward: AdReward | null = null;
  private config: AdProviderConfig;
  private isInit = false;
  private admobModule: AdMobModule | null = null;

  constructor(config: AdProviderConfig) {
    this.config = config;
  }

  get status(): AdStatus {
    return this._status;
  }

  async initialize(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;

    try {
      this.admobModule = await import('@capacitor-community/admob');
      const { AdMob } = this.admobModule;

      await AdMob.initialize({
        initializeForTesting: this.config.testMode ?? false,
      });

      this.isInit = true;
      console.log('[AdMobProvider] Initialized');
      return true;
    } catch (error) {
      console.error('[AdMobProvider] Failed to initialize:', error);
      return false;
    }
  }

  isRewardedAdReady(): boolean {
    return this._status === 'ready';
  }

  async loadRewardedAd(_rewardType?: AdRewardType): Promise<boolean> {
    if (!this.isInit || !this.admobModule) return false;
    if (this._status === 'loading') return false;

    const platform = Capacitor.getPlatform();
    let adId: string;

    if (platform === 'android') {
      adId = this.config.admobAndroidRewardedAdUnitId || 'ca-app-pub-5244313691595903/0000000000';
    } else if (platform === 'ios') {
      adId = this.config.admobIosRewardedAdUnitId || 'ca-app-pub-5244313691595903/0000000000';
    } else {
      return false;
    }

    this._status = 'loading';

    try {
      const { AdMob } = this.admobModule;
      await AdMob.prepareRewardVideoAd({ adId });
      this._status = 'ready';
      return true;
    } catch (error) {
      console.error('[AdMobProvider] Failed to load ad:', error);
      this._status = 'error';
      return false;
    }
  }

  async showRewardedAd(_rewardType?: AdRewardType): Promise<AdReward | null> {
    if (!this.isInit || !this.admobModule) return null;

    if (this._status !== 'ready') {
      const loaded = await this.loadRewardedAd(_rewardType);
      if (!loaded) return null;
    }

    const { AdMob, RewardAdPluginEvents } = this.admobModule;

    this._status = 'showing';

    return new Promise(async (resolve) => {
      let rewarded = false;
      let rewardHandle: { remove: () => Promise<void> } | null = null;
      let dismissHandle: { remove: () => Promise<void> } | null = null;
      let failHandle: { remove: () => Promise<void> } | null = null;

      const cleanup = async () => {
        if (rewardHandle) await rewardHandle.remove();
        if (dismissHandle) await dismissHandle.remove();
        if (failHandle) await failHandle.remove();
      };

      rewardHandle = await AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
        rewarded = true;
      });

      dismissHandle = await AdMob.addListener(RewardAdPluginEvents.Dismissed, async () => {
        await cleanup();
        this._status = 'idle';
        if (rewarded && this.currentReward) {
          const reward = this.currentReward;
          this.currentReward = null;
          resolve(reward);
        } else {
          resolve(null);
        }
      });

      failHandle = await AdMob.addListener(RewardAdPluginEvents.FailedToShow, async () => {
        await cleanup();
        this._status = 'idle';
        resolve(null);
      });

      try {
        await AdMob.showRewardVideoAd();
      } catch {
        await cleanup();
        this._status = 'idle';
        resolve(null);
      }
    });
  }

  setReward(reward: AdReward): void {
    this.currentReward = reward;
  }

  destroy(): void {
    this._status = 'idle';
    this.currentReward = null;
    this.isInit = false;
    this.admobModule = null;
  }
}
